import { buildWorkflowTimeline } from '../streaming/buildWorkflowTimeline.js'
import { B2B_COPILOT_PLAN_USER_PROMPT } from './b2bCopilotPlanDemoContent.js'
import { GCP_HOSTING_USER_PROMPT } from './gcpHostingDemoContent.js'
import { WEBSITE_CRM_USER_PROMPT } from './websiteCrmDemoContent.js'
import { B2B_COPILOT_PLAN_DEMO_ARTIFACT_ID, B2B_COPILOT_PLAN_ZIP_ARTIFACT_ID } from './b2bCopilotPlanDemo.js'
import { WEBSITE_CRM_DEMO_DOC_ARTIFACT_ID, WEBSITE_CRM_DEMO_HTML_ARTIFACT_ID } from './websiteCrmDemo.js'
import { ZOHO_CRM_INTEGRATION_DEMO_ARTIFACT_ID } from './zohoCrmIntegrationDemo.js'
import {
  NEXUS_CONNECTED_APP_BLOCKS,
  NEXUS_CRM_INTEGRATION_ARTIFACT_FILE_NAME,
  NEXUS_JOURNEY_INTEGRATIONS_TABLE,
} from './nexusIntegrationsDemo.js'

export const NEXUS_DEMO_JOURNEY_THOUGHT_DETAILS = [
  `The user confirmed DNS nameserver updates with **"done"**. I'll verify propagation and compile a structured summary of the full **NexusB2B** journey — development plan, website + integrations, and Google Cloud deployment.`,
  `I'll organize everything into tables: *conversation timeline*, *documents produced*, *integrations*, *infrastructure*, and *end-to-end execution results*.`,
]

export const NEXUS_DEMO_JOURNEY_INTRO_MARKDOWN = `✅ **DNS propagation verified** — nameservers are pointing to Google Cloud and SSL is active on \`app.nexusb2b.com\`.

Here is a structured summary of everything we accomplished across your **three requests**:

- **Step 1** — Development plan from GitHub \`requirement.md\`
- **Step 2** — Procurement website, **Salesforce CRM**, **HubSpot SRM**, **Procurify**, Stripe/Mailchimp, and demo-user test
- **Step 3** — Full **9-phase Google Cloud** deployment to production`

export const NEXUS_DEMO_CONVERSATION_JOURNEY_TABLE = [
  {
    Step: '**1 — Development Plan**',
    'Your Request': B2B_COPILOT_PLAN_USER_PROMPT,
    Deliverable: '*B2B Copilot — Development Plan.md* (requirements summary, suggested changes, phased roadmap)',
    Status: '**✅ Complete**',
  },
  {
    Step: '**2 — Website & CRM**',
    'Your Request': WEBSITE_CRM_USER_PROMPT,
    Deliverable:
      'NexusB2B procurement site + **Salesforce CRM** + **HubSpot SRM** + **Procurify** + demo-user test + live triggers',
    Status: '**✅ Complete**',
  },
  {
    Step: '**3 — Google Cloud Hosting**',
    'Your Request': GCP_HOSTING_USER_PROMPT,
    Deliverable: 'Full-stack deployment on GCP *(9 phases)* — frontend, backend, database, HTTPS, WAF, CI/CD',
    Status: '**✅ Complete**',
  },
]

export const NEXUS_DEMO_DOCUMENTS_TABLE = [
  {
    Document: '**B2B Copilot — Development Plan.md**',
    Format: 'Markdown',
    Purpose: 'Requirements analysis, change recommendations, and phased build plan',
    'Produced In': 'Step 1',
  },
  {
    Document: '**B2B Copilot.zip**',
    Format: 'Zip archive',
    Purpose: 'Scaffolded b2b-copilot repository bundle for local setup',
    'Produced In': 'Step 1',
  },
  {
    Document: '**NexusB2B — Procurement Site.html**',
    Format: 'HTML App',
    Purpose: 'Live procurement website *(hero, catalog, RFQ flow)*',
    'Produced In': 'Step 2',
  },
  {
    Document: `**${NEXUS_CRM_INTEGRATION_ARTIFACT_FILE_NAME}**`,
    Format: 'Markdown',
    Purpose: 'FastAPI Salesforce CRM API layer and endpoint reference',
    'Produced In': 'Step 2',
  },
  {
    Document: '**NexusB2B — CRM Test Report.md**',
    Format: 'Markdown',
    Purpose: 'Demo-user checkout test and CRM + procurement trigger verification',
    'Produced In': 'Step 2',
  },
]

export const NEXUS_DEMO_INTEGRATIONS_TABLE = NEXUS_JOURNEY_INTEGRATIONS_TABLE

export const NEXUS_DEMO_GCP_INFRASTRUCTURE_TABLE = [
  { Component: '**GCP Project**', Status: '**✅ Live**', Details: 'nexus-b2b-prod-48291' },
  { Component: '**Database (Cloud SQL)**', Status: '**✅ Live**', Details: 'Private PostgreSQL' },
  { Component: '**Backend (Cloud Run)**', Status: '**✅ Live**', Details: 'https://bizlink-backend-a8f2c.run.app' },
  { Component: '**Frontend (Cloud Storage + CDN)**', Status: '**✅ Live**', Details: 'React app deployed' },
  { Component: '**Load Balancer + Cloud Armor**', Status: '**✅ Live**', Details: 'WAF protection enabled' },
  { Component: '**Custom Domain + SSL**', Status: '**✅ Live**', Details: 'https://app.nexusb2b.com' },
  { Component: '**Cloud Monitoring**', Status: '**✅ Live**', Details: 'Uptime + error alerts' },
  { Component: '**CI/CD (Cloud Build)**', Status: '**✅ Live**', Details: 'Push-to-deploy on `main`' },
  { Component: '**Est. Monthly Cost**', Status: '*~$55–65*', Details: 'Within original estimate' },
]
